import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClientWrapper } from 'hooks';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';

import styles from './ClientDocuments.module.scss';
import DocumentTable from './Table/DocumentTable';
import UploadDocument from './Upload/UploadDocument';
import EditDocument from './Edit/EditDocument';
import DeleteDocument from './Delete/DeleteDocument';
import { DocumentAttachment } from '@spike/client-model';
import { useDispatch, useSelector } from 'react-redux';
import {
    attachFilesThunk,
    ClientsState,
    ClientsStatus,
    deleteFileThunk,
    downloadFileThunk,
    listDocumentsThunk,
    renameFileThunk
} from '@spike/clients-action';
import { RootState } from 'store';
import { Spinner } from 'components/UI';
import { ClientEditHeader } from 'components/Client/ClientEditHeader';
import { useMediaQuery } from 'react-responsive';
import Client from '@spike/client-model';

const getBaseName = (name: string): string => {
    const lastDot = name.lastIndexOf('.');
    return lastDot > 0 ? name.slice(0, lastDot) : name;
};

export interface ClientDocumentsProps {
    clientId: number;
    clientRef: Client; // we need the whole client object to get the documents and status, but we only use the id for actions, so we can pass the whole client as a prop without causing unnecessary re-renders
    // the parent is responsible for navigating/book actions, so these are required
    onBook: () => void;
    onNewSale?: () => void;
    onClose: () => void;
    onSelect: (key: string) => void;
}

export const ClientDocuments: React.FC<ClientDocumentsProps> = ({
    clientId,
    clientRef,
    onBook,
    onNewSale,
    onClose,
    onSelect
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const isMobile = useMediaQuery({ query: '(max-width:768px)' });

    const [documents, setDocuments] = useState<DocumentAttachment[]>([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const clientStatus = useSelector<RootState, ClientsStatus>(state => state.clients.status);
    const client = useSelector<RootState, ClientsState | undefined>(state => state.clients);

    //const { documents, setDocuments, loading, refetch } = useClientDocuments(clientId);
    const [searchText, setSearchText] = useState('');
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [stagedFiles, setStagedFiles] = useState<File[]>([]);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
    const [renameModalDoc, setRenameModalDoc] = useState<DocumentAttachment | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [deleteConfirmDoc, setDeleteConfirmDoc] = useState<DocumentAttachment | null>(null);
    const [saving, setSaving] = useState(false);

    const [page, setPage] = useState<number>(1);
    const [size, setSize] = useState<number>(10);
    const [totalPages, setTotalPages] = useState<number>(0);

    const fetchDocuments = useCallback(() => {
        setLoading(true);
        dispatch(listDocumentsThunk(apiClientWrapper, clientId, searchText, page, size));
    }, [clientId, searchText, page, size]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    useEffect(() => {
        switch (clientStatus) {
            case ClientsStatus.DocumentsListSuccess:
                setInitialLoading(false);
                setDocuments(client?.documents || []);

                setPage(Number(client?.page) || page);
                setSize(Number(client?.size) || size);
                setTotalPages(Number(client?.total_pages) || 0);

                setLoading(false);
                break;

            case ClientsStatus.DocumentsAttachSuccess:
                setSaving(false);
                setAddModalOpen(false);
                setStagedFiles([]);
                fetchDocuments();
                break;

            case ClientsStatus.DocumentsListing:
                setLoading(false);
                break;

            case ClientsStatus.DocumentDownloading:
                setLoading(true);
                break;

            case ClientsStatus.DocumentDownloadSuccess:
                setLoading(false);
                break;

            case ClientsStatus.DocumentDeleteSuccess:
                setDeleteConfirmDoc(null);
                fetchDocuments();
                break;

            case ClientsStatus.DocumentRenaming:
                setSaving(true);
                break;

            case ClientsStatus.DocumentRenameSuccess:
                setSaving(false);
                setRenameModalDoc(null);
                setRenameValue('');
                fetchDocuments();
                break;

            case ClientsStatus.Error:
                setLoading(false);
                setSaving(false);
                break;

            default:
                break;
        }
    }, [clientStatus]);

    const handleOpenAddModal = useCallback(() => {
        setAddModalOpen(true);
        setStagedFiles([]);
        setUploadError(null);
    }, []);

    const handleCloseAddModal = useCallback(() => {
        if (stagedFiles.length > 0) {
            setExitConfirmOpen(true);
        } else {
            setAddModalOpen(false);
        }
    }, [stagedFiles.length]);

    const handleConfirmExitAddModal = useCallback(() => {
        setExitConfirmOpen(false);
        setAddModalOpen(false);
        setStagedFiles([]);
        setUploadError(null);
    }, []);

    const removeStagedFile = useCallback((index: number) => {
        setStagedFiles(prev => prev.filter((_, i) => i !== index));
        setUploadError(null);
    }, []);

    const handleAddFiles = useCallback((files: File[]) => {
        console.log(
            '[ClientDocuments] handleAddFiles',
            files.map(f => f.name)
        );
        setStagedFiles(prev => [...prev, ...files]);
        setUploadError(null);
    }, []);

    const handleSaveUpload = useCallback(
        (files: File[]) => {
            if (files.length === 0) return;
            setUploadError(null);
            setSaving(true);
            dispatch(attachFilesThunk(apiClientWrapper, clientId, files));
        },
        [clientId, fetchDocuments, page, size, setDocuments]
    );

    const handleDownload = useCallback((doc: DocumentAttachment) => {
        // if (doc.url) window.open(doc.url, '_blank');
        dispatch(downloadFileThunk(apiClientWrapper, clientId, doc.id, doc.filename));
    }, []);

    const handleRenameOpen = useCallback((doc: DocumentAttachment) => {
        setRenameModalDoc(doc);
        setRenameValue(getBaseName(doc.filename));
    }, []);

    const handleRenameSave = useCallback(() => {
        if (!renameModalDoc) return;

        const newName = renameValue.trim() ? `${renameValue.trim()}` : renameModalDoc.filename;
        dispatch(renameFileThunk(apiClientWrapper, clientId, renameModalDoc!.id, newName));
    }, [renameModalDoc, renameValue, fetchDocuments, page, size, setDocuments]);

    const handleDeleteConfirmOpen = useCallback((doc: DocumentAttachment) => {
        setDeleteConfirmDoc(doc);
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (!deleteConfirmDoc) return;
        dispatch(deleteFileThunk(apiClientWrapper, clientId, deleteConfirmDoc.id));
    }, [deleteConfirmDoc, fetchDocuments, page, size, setDocuments]);

    const isEmpty = !loading && documents.length === 0;

    const renderContent = () => {
        if (initialLoading) {
            return (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            );
        }

        return (
            <div className={styles.root}>
                {isMobile && client && (
                    <div className={styles.editWrapper}>
                        <ClientEditHeader
                            client={clientRef}
                            onSelect={onSelect}
                            onBook={onBook}
                            onNewSale={onNewSale}
                            onClose={onClose}
                        />
                    </div>
                )}
                <div className={styles.headWrapper}>
                    <DefaultHeader
                        title="Documents"
                        buttonLabel="Add File"
                        displayButton
                        buttonIcon={faPlus}
                        onClick={handleOpenAddModal}
                        id="client_documents_add_file"
                        className={styles.headerContainer}
                    />
                    <SearchBar
                        searchText={searchText}
                        onChangeSearchText={v => {
                            setSearchText(v);
                            setPage(1);
                        }}
                        placeholder="Search by file name"
                        className={styles.searchBarContainer}
                    />
                </div>

                <DocumentTable
                    documents={documents}
                    loading={loading}
                    isEmpty={isEmpty}
                    page={page}
                    totalPages={totalPages}
                    size={size}
                    onPageChange={setPage}
                    onSizeChange={setSize}
                    onDownload={handleDownload}
                    onRename={handleRenameOpen}
                    onDelete={handleDeleteConfirmOpen}
                />
                {addModalOpen && (
                    <UploadDocument
                        onClose={handleCloseAddModal}
                        onSave={handleSaveUpload}
                        onExitConfirm={handleConfirmExitAddModal}
                        isSaving={saving}
                        uploadError={uploadError}
                        stagedFiles={stagedFiles}
                        clientId={clientId}
                        onRemoveFile={removeStagedFile}
                        onAddFiles={handleAddFiles}
                        exitConfirmOpen={exitConfirmOpen}
                        onExitCancel={() => setExitConfirmOpen(false)}
                    />
                )}

                {/* Rename modal */}
                {renameModalDoc && (
                    <EditDocument
                        doc={renameModalDoc}
                        value={renameValue}
                        isLoading={saving}
                        onChange={setRenameValue}
                        onClose={() => {
                            setRenameModalDoc(null);
                            setRenameValue('');
                        }}
                        onSave={handleRenameSave}
                    />
                )}

                {/* Delete confirmation */}
                {deleteConfirmDoc && (
                    <DeleteDocument
                        doc={deleteConfirmDoc}
                        onConfirm={handleDeleteConfirm}
                        onCancel={() => setDeleteConfirmDoc(null)}
                    />
                )}
            </div>
        );
    };

    return renderContent();
};

export default ClientDocuments;
